import React, { useContext, useEffect, useState } from "react";

import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import {
  MarketplaceProductAttributeJob,
  Status,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_marketplace_pb";

import {
  createMarketplaceProductAttributeJob,
  getMarketplaceProductAttributeJobResult,
  getMarketplaceProductAttributeJobs,
} from "api/endpoints/marketplace";
import { gRPCStatus } from "api/grpc-status-codes";
import { callOptionsHeaders } from "api/helpers";
import AddJobForm, {
  NewJobFormState,
} from "components/marketplace/add-job-form";
import { DEFAULT_REGION_CODE } from "constants/index";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";

const mapStatusToName = {
  [Status.STARTED]: "Started",
  [Status.FAILED]: "Failed",
  [Status.COMPLETED]: "Completed",
  [Status.UNSPECIFIED]: "Unspecified",
};

const defaultFormState: NewJobFormState = {
  name: "",
  urlListStr: "",
  extractAttributesGroupId: "",
  generateAttributeIds: [],
};

type ProductAttributeJobRow = Pick<
  MarketplaceProductAttributeJob,
  "jobId" | "status" | "createdDate" | "name" | "urlCount"
>;

const MarketplaceJobs = () => {
  const { showError } = useContext(SnackbarContext);
  const [jobsData, setJobsData] = useState<ProductAttributeJobRow[]>([]);
  const [openAddJobDialog, setOpenAddJobDialog] = useState(false);
  const [newJobFormState, setNewJobFormState] = useState(defaultFormState);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      const jobsData = await getMarketplaceProductAttributeJobs(
        callOptionsHeaders(DEFAULT_REGION_CODE),
      );
      setIsLoading(false);
      setJobsData(jobsData.jobs);
    };
    init();
  }, []);

  const handleAddJob = async () => {
    setIsLoading(true);
    try {
      const urlList = newJobFormState.urlListStr
        .split(",")
        .map((s) => s.trim());

      const jobData = await createMarketplaceProductAttributeJob(
        {
          name: newJobFormState.name,
          urlList,
          extractAttributesGroupId: newJobFormState.extractAttributesGroupId,
          generateAttributeIds: newJobFormState.generateAttributeIds,
        },
        callOptionsHeaders(DEFAULT_REGION_CODE),
      );
      const job = jobData.job;

      if (!job) {
        showError({
          code: gRPCStatus.UNKNOWN,
          name: "No Job returned",
          message:
            "Unknown error occurred adding a marketplace job. Please try again later.",
        });
      } else {
        setJobsData([
          {
            jobId: job.jobId,
            name: job.name,
            createdDate: job.createdDate,
            urlCount: job.urlCount,
            status: job.status,
          },
          ...jobsData,
        ]);
      }
    } catch (error) {
      showError(error as GrpcError);
    } finally {
      setOpenAddJobDialog(false);
      setNewJobFormState(defaultFormState);
      setIsLoading(false);
    }
  };

  const handleJobDataDownload = async (jobId: string) => {
    const jobResultData = await getMarketplaceProductAttributeJobResult(
      { jobId },
      callOptionsHeaders(DEFAULT_REGION_CODE),
    );

    window.open(jobResultData.resultUrl, "_blank");
  };

  const renderJobRecord = (row: ProductAttributeJobRow) => (
    <TableRow key={`run-${row.jobId}`}>
      <TableCell align="center" style={{ verticalAlign: "top" }}>
        {row.name}
      </TableCell>
      <TableCell align="center" style={{ verticalAlign: "top" }}>
        {row.createdDate &&
          formatDateTimeDisplay(convertTimestampToDate(row.createdDate))}
      </TableCell>
      <TableCell align="center" style={{ verticalAlign: "top" }}>
        {row.urlCount}
      </TableCell>
      <TableCell align="center" style={{ verticalAlign: "top" }}>
        {mapStatusToName[row.status]}
      </TableCell>
      <TableCell align="center" style={{ verticalAlign: "top" }}>
        {row.status === Status.COMPLETED && (
          <Button
            data-testid={`download-result-${row.jobId}`}
            onClick={() => {
              handleJobDataDownload(row.jobId);
            }}
          >
            Download
          </Button>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} style={{ paddingBottom: "20px" }}>
          <Grid container alignContent="space-between" alignItems="center">
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between">
                <Typography
                  variant="h5"
                  color="textPrimary"
                  gutterBottom
                  data-testid="ai-tool-job-runs-title"
                >
                  AI Tool Job Runs
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setOpenAddJobDialog(true)}
                  data-testid="add-job-dialog-btn"
                >
                  Add Job
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table aria-label="crowndin-templates">
              <TableHead>
                <TableRow>
                  <TableCell align="center">
                    <b>Name</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Created Date</b>
                  </TableCell>
                  <TableCell align="center">
                    <b># URLs</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Status</b>
                  </TableCell>
                  <TableCell align="center">
                    <b>Actions</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  jobsData.map((row) => renderJobRecord(row))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <AddJobForm
        isOpen={openAddJobDialog}
        handleClose={() => {
          setOpenAddJobDialog(false);
        }}
        newJobFormState={newJobFormState}
        setNewJobFormState={setNewJobFormState}
        handleSubmit={() => {
          setIsLoading(true);
          handleAddJob();
        }}
        isLoading={isLoading}
        isDisabled={
          !newJobFormState.name ||
          !newJobFormState.urlListStr ||
          !newJobFormState.extractAttributesGroupId ||
          !newJobFormState.generateAttributeIds.length
        }
      />
    </>
  );
};

export default MarketplaceJobs;
